// Copyright © 2019-2026 Focal Systems. All rights reserved.

export type DayPart = "morning" | "afternoon" | "evening" | "night";

/** 05–11 morning, 12–16 afternoon, 17–21 evening, otherwise night. */
export function dayPart(hour: number): DayPart {
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 22) return "evening";
  return "night";
}

/** Greeting keys under `home.greeting`, each taking a `{{name}}`. */
const DAY_PART_GREETINGS: Record<DayPart, readonly string[]> = {
  morning: ["morning.plain", "morning.coffee", "morning.riseAndShine"],
  afternoon: ["afternoon.plain", "afternoon.halfway", "afternoon.lunch"],
  evening: ["evening.plain", "evening.wrappingUp", "evening.stillAtIt"],
  night: ["night.midnightOil", "night.lateOne", "night.quietHours"],
};

/** Extras mixed into the pool on particular days, so a Monday morning can say so. */
function weekdayGreetings(weekday: number, part: DayPart): string[] {
  if (weekday === 0 || weekday === 6) return ["weekday.weekend"];
  if (weekday === 1 && part === "morning") return ["weekday.monday"];
  if (weekday === 5 && (part === "afternoon" || part === "evening")) return ["weekday.friday"];
  return [];
}

const HALLOWEEN_BEFORE = [
  "halloweenBefore.wicked",
  "halloweenBefore.trickOrTreat",
  "halloweenBefore.costume",
  "halloweenBefore.pumpkins",
  "halloweenBefore.ghosts",
];
const HALLOWEEN_AFTER = [
  "halloweenAfter.survived",
  "halloweenAfter.sugarCrash",
  "halloweenAfter.backFromTheDead",
  "halloweenAfter.leftovers",
];

/**
 * Seasonal pools mixed in with the everyday greetings for a few days, by local date:
 * Halloween build-up from 27 October (four days before) through the day itself, then 1–4 November.
 */
function seasonalGreetings(now: Date): readonly string[] | null {
  const month = now.getMonth();
  const day = now.getDate();
  if (month === 9 && day >= 27) return HALLOWEEN_BEFORE;
  if (month === 10 && day <= 4) return HALLOWEEN_AFTER;
  return null;
}

/** Every key `pickGreeting` can return, for checking the locales carry them all. */
export const ALL_GREETING_KEYS = [
  ...Object.values(DAY_PART_GREETINGS).flat(),
  "weekday.weekend",
  "weekday.monday",
  "weekday.friday",
  ...HALLOWEEN_BEFORE,
  ...HALLOWEEN_AFTER,
];

/**
 * A random greeting key (under `home.greeting`) that fits the local time, weekday and season of `now`.
 * In a seasonal window it's 50-50: the lower half of the draw picks a seasonal line, the upper half
 * an everyday one, so a single draw stays uniform within each pool.
 */
export function pickGreeting(now: Date, random: () => number = Math.random): string {
  const part = dayPart(now.getHours());
  const everyday = [...DAY_PART_GREETINGS[part], ...weekdayGreetings(now.getDay(), part)];
  const seasonal = seasonalGreetings(now);
  let roll = random();
  if (seasonal) {
    if (roll < 0.5) return seasonal[Math.floor(roll * 2 * seasonal.length)] ?? seasonal[0] ?? "morning.plain";
    roll = (roll - 0.5) * 2;
  }
  return everyday[Math.floor(roll * everyday.length)] ?? DAY_PART_GREETINGS[part][0] ?? "morning.plain";
}

/** The name to greet: the first word of the full name, else the email's local part. */
export function greetingName(fullName: string | undefined, email: string | undefined): string {
  const first = fullName?.trim().split(/\s+/)[0];
  if (first) return first;
  return email?.split("@")[0] ?? "";
}
